import api from './api';
import { Ramo, Linea, Clase, PagedResult, UnidadMedida, CreateUnidadMedidaRequest, UpdateUnidadMedidaRequest } from '../types';
import configuracionService from './configuracionService';
import { getFileNameFromContentDisposition } from '../utils/contentDisposition';

function rutaRdlcDesdeRespuesta(data: Record<string, unknown> | undefined | null): string {
  if (!data) return '';
  const ruta =
    data.rutaRdlc
    ?? data.RutaRdlc
    ?? data.archCodBarra
    ?? data.ArchCodBarra
    ?? '';
  return String(ruta).trim();
}

const SECCION_ETIQUETAS = 'INVENTARIO';
const CAMPO_ETIQUETAS_GENERAL = 'ARCHCODBARRA';

export const catalogosService = {
  // Ramos
  getRamos: async (pageNumber: number, pageSize: number, searchTerm?: string): Promise<PagedResult<Ramo>> => {
    const response = await api.get<PagedResult<Ramo>>('/api/catalogos/ramos', {
      params: { pageNumber, pageSize, searchTerm }
    });
    return response.data;
  },

  getRamo: async (codigoRamo: string): Promise<Ramo> => {
    const response = await api.get<Ramo>(`/api/catalogos/ramos/${codigoRamo}`);
    return response.data;
  },

  createRamo: async (ramo: Ramo): Promise<Ramo> => {
    const response = await api.post<Ramo>('/api/catalogos/ramos', ramo);
    return response.data;
  },

  updateRamo: async (codigoRamo: string, ramo: Ramo): Promise<Ramo> => {
    const response = await api.put<Ramo>(`/api/catalogos/ramos/${codigoRamo}`, ramo);
    return response.data;
  },

  deleteRamo: async (codigoRamo: string): Promise<void> => {
    await api.delete(`/api/catalogos/ramos/${codigoRamo}`);
  },

  // Líneas
  getLineas: async (
    pageNumber: number, 
    pageSize: number, 
    searchTerm?: string, 
    codigoRamo?: string
  ): Promise<PagedResult<Linea>> => {
    const response = await api.get<PagedResult<Linea>>('/api/catalogos/lineas', {
      params: { 
        pageNumber, 
        pageSize, 
        searchTerm, 
        codigoRamo 
      }
    });
    return response.data;
  },

  getLinea: async (codigoLinea: string): Promise<Linea> => {
    const response = await api.get<Linea>(`/api/catalogos/lineas/${codigoLinea}`);
    return response.data;
  },

  createLinea: async (linea: Linea): Promise<Linea> => {
    const response = await api.post<Linea>('/api/catalogos/lineas', linea);
    return response.data;
  },

  updateLinea: async (codigoLinea: string, linea: Linea): Promise<Linea> => {
    const response = await api.put<Linea>(`/api/catalogos/lineas/${codigoLinea}`, linea);
    return response.data;
  },

  deleteLinea: async (codigoLinea: string): Promise<void> => {
    await api.delete(`/api/catalogos/lineas/${codigoLinea}`);
  },

  // Clases
  getClases: async (pageNumber: number, pageSize: number, searchTerm?: string, codigoRamo?: string, codigoLinea?: string): Promise<PagedResult<Clase>> => {
    const response = await api.get<PagedResult<Clase>>('/api/catalogos/clases', {
      params: { pageNumber, pageSize, searchTerm, codigoRamo, codigoLinea }
    });
    return response.data;
  },

  getClase: async (codigoClase: string): Promise<Clase> => {
    const response = await api.get<Clase>(`/api/catalogos/clases/${codigoClase}`);
    return response.data;
  },

  createClase: async (clase: Clase): Promise<Clase> => {
    const response = await api.post<Clase>('/api/catalogos/clases', clase);
    return response.data;
  },

  updateClase: async (codigoClase: string, clase: Clase): Promise<Clase> => {
    const response = await api.put<Clase>(`/api/catalogos/clases/${codigoClase}`, clase);
    return response.data;
  },

  deleteClase: async (codigoClase: string): Promise<void> => {
    await api.delete(`/api/catalogos/clases/${codigoClase}`);
  },

  // Unidades de medida
  getUnidadesMedida: async (
    pageNumber: number,
    pageSize: number,
    searchTerm?: string
  ): Promise<PagedResult<UnidadMedida>> => {
    const response = await api.get<PagedResult<UnidadMedida>>('/api/catalogos/unidades-medida', {
      params: { pageNumber, pageSize, searchTerm }
    });
    return response.data;
  },

  getUnidadMedida: async (id: string): Promise<UnidadMedida> => {
    const response = await api.get<UnidadMedida>(
      `/api/catalogos/unidades-medida/${encodeURIComponent(id)}`
    );
    return response.data;
  },

  createUnidadMedida: async (body: CreateUnidadMedidaRequest): Promise<UnidadMedida> => {
    const response = await api.post<UnidadMedida>('/api/catalogos/unidades-medida', body);
    return response.data;
  },

  updateUnidadMedida: async (id: string, body: UpdateUnidadMedidaRequest): Promise<void> => {
    await api.put(`/api/catalogos/unidades-medida/${encodeURIComponent(id)}`, body);
  },

  deleteUnidadMedida: async (id: string): Promise<void> => {
    await api.delete(`/api/catalogos/unidades-medida/${encodeURIComponent(id)}`);
  },

  /** Ruta del RDLC general del tenant (config INVENTARIO / ARCHCODBARRA). */
  getRdlcEtiquetasGeneral: async (): Promise<string> => {
    const config = await configuracionService.getByKey(SECCION_ETIQUETAS, CAMPO_ETIQUETAS_GENERAL);
    return (config?.valor || '').trim();
  },

  updateRdlcEtiquetasGeneral: async (rutaRdlc: string): Promise<void> => {
    const dto = {
      usuario: '',
      seccion: SECCION_ETIQUETAS,
      campo: CAMPO_ETIQUETAS_GENERAL,
      valor: rutaRdlc.trim(),
      comentario: 'RDLC general de etiquetas de código de barras',
    };
    const existente = await configuracionService.getByKey(SECCION_ETIQUETAS, CAMPO_ETIQUETAS_GENERAL);
    if (existente) {
      await configuracionService.updateConfiguracion({
        ...existente,
        valor: dto.valor,
        comentario: dto.comentario,
      });
      return;
    }
    try {
      await configuracionService.createConfiguracion(dto);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        await configuracionService.updateConfiguracion(dto);
        return;
      }
      throw error;
    }
  },

  /**
   * Sube el RDLC de etiquetas al storage del tenant (JWT).
   * No envía idCompania ni nroSuc: el API resuelve tenant y, si hace falta, compañía.
   */
  uploadRdlcEtiquetas: async (
    archivo: File,
  ): Promise<{ rutaRdlc: string; nombreArchivo: string }> => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    const response = await api.post<Record<string, unknown>>(
      '/api/catalogos/rdlc-etiquetas/upload',
      formData,
    );
    const rutaRdlc = rutaRdlcDesdeRespuesta(response.data);
    const nombreArchivo = String(
      response.data?.nombreArchivo ?? response.data?.NombreArchivo ?? archivo.name,
    ).trim();
    return { rutaRdlc, nombreArchivo };
  },

  downloadRdlcEtiquetas: async (rutaRdlc: string): Promise<void> => {
    const response = await api.get('/api/catalogos/rdlc-etiquetas/download', {
      params: { ruta: rutaRdlc.trim() },
      responseType: 'blob',
    });
    const cd = response.headers['content-disposition'] as string | undefined;
    const fileName =
      getFileNameFromContentDisposition(cd)
      || rutaRdlc.replace(/\\/g, '/').split('/').pop()
      || 'etiquetas.rdlc';
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  },
}; 